import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Icon } from "@/components/Icon"
import { buildSelectionMessage, suggestionsFor } from "@/features/oio/lib/selectionPrompt"

interface SelectionToolbarProps {
  top: number
  left: number
  selectedText: string
  onSubmit: (message: string) => void
  onExpandedChange?: (expanded: boolean) => void
}

export function SelectionToolbar({
  top,
  left,
  selectedText,
  onSubmit,
  onExpandedChange,
}: SelectionToolbarProps) {
  const [expanded, setExpanded] = useState(false)
  const [value, setValue] = useState("")
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const suggestions = useMemo(() => suggestionsFor(selectedText), [selectedText])

  // The officer types only their question — the selected passage is shown as
  // a quote above the box and stitched into the outgoing message on send.
  useEffect(() => {
    if (expanded) {
      setValue("")
      // Use a microtask to ensure the textarea is mounted before focusing
      requestAnimationFrame(() => {
        textareaRef.current?.focus()
      })
    }
  }, [expanded, selectedText])

  const handleOpen = useCallback(() => {
    setExpanded(true)
    onExpandedChange?.(true)
  }, [onExpandedChange])

  const handleCancel = useCallback(() => {
    setExpanded(false)
    setValue("")
    onExpandedChange?.(false)
  }, [onExpandedChange])

  const handleSend = useCallback(() => {
    const question = value.trim()
    if (!question) return
    onSubmit(buildSelectionMessage(selectedText, question))
    setExpanded(false)
    setValue("")
    onExpandedChange?.(false)
  }, [value, selectedText, onSubmit, onExpandedChange])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  return (
    <div
      className="oio-selection-toolbar"
      style={{
        position: "absolute",
        top,
        left,
        transform: "translate(-50%, -100%)",
        marginTop: "-8px", // extra clearance
        zIndex: 100,
        flexDirection: "column",
      }}
      data-testid="selection-toolbar"
      // Without this, mousedown on the toolbar (which sits outside the
      // selected text) collapses the browser selection before the click can
      // register, unmounting the toolbar mid-click — the buttons never fire.
      onMouseDown={(e) => e.preventDefault()}
    >
      <button
        className="btn btn--sm btn--primary"
        onClick={handleOpen}
        data-testid="selection-action-btn"
        style={{ display: expanded ? "none" : undefined }}
      >
        <Icon name="sparkle" size={14} /> Ask AI
      </button>

      {expanded && (
        <div className="selection-mini-composer">
          <div className="selection-mini-composer__quote" data-testid="selection-quote">
            <span className="lbl">Selected from the draft</span>
            <p>{selectedText}</p>
          </div>
          <div className="selection-mini-composer__suggestions">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                className="chip"
                onClick={() => {
                  setValue(s)
                  textareaRef.current?.focus()
                }}
                data-testid="selection-suggestion"
              >
                {s}
              </button>
            ))}
          </div>
          <textarea
            ref={textareaRef}
            className="selection-mini-composer__input"
            rows={4}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this selection…"
            data-testid="selection-mini-input"
          />
          <div className="selection-mini-composer__actions">
            <button
              className="btn btn--sm btn--ghost"
              onClick={handleCancel}
              data-testid="selection-cancel-btn"
            >
              Cancel
            </button>
            <button
              className="btn btn--sm btn--primary"
              onClick={handleSend}
              disabled={!value.trim()}
              data-testid="selection-send-btn"
            >
              Send ↑
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
